import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCountryConfig } from "../hooks/useCountryConfig";
import {
  Users, BarChart3, Plus, Trash2, RefreshCw,
  TrendingUp, TrendingDown, Target, Layers,
  Edit2, Check, X,
  AlertTriangle, CheckCircle2, Zap,
} from "lucide-react";
import i18n from "../i18n";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "plans",     icon: Target },
  { id: "headcount", icon: Users },
  { id: "scenarios", icon: Layers },
];

const STATUS_STYLE = {
  draft:  "bg-gray-500/15 text-gray-400 border-gray-500/30",
  active: "bg-green-500/15 text-green-400 border-green-500/30",
  locked: "bg-blue-500/15 text-blue-400 border-blue-500/30",
};

const PRIORITY_STYLE = {
  high:   "text-red-400 bg-red-500/10 border-red-500/20",
  medium: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  low:    "text-green-400 bg-green-500/10 border-green-500/20",
};

const fmt = (n, cur = "TND", locale = i18n.language) => n == null ? "—" : `${Number(n).toLocaleString(locale)} ${cur}`;
const statusLabel = (status, t) => t(`workforcePlanningHub.status.${status}`, { defaultValue: status });
const priorityLabel = (priority, t) => t(`workforcePlanningHub.priorities.${priority}`, { defaultValue: priority });

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("v1/workforce-planning/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center py-20 text-[var(--lm-muted)]">{t("workforcePlanningHub.loading")}</div>;

  const gap = data.headcount_gap;
  const kpis = [
    { label: t("workforcePlanningHub.dashboard.actualHeadcount"),   value: data.actual_headcount,             icon: Users,     color: "blue" },
    { label: t("workforcePlanningHub.dashboard.plannedHeadcount"),  value: data.planned_headcount || "—",     icon: Target,    color: "purple" },
    { label: t("workforcePlanningHub.dashboard.createdPlans"),      value: data.total_plans,                  icon: BarChart3, color: "green" },
    { label: t("workforcePlanningHub.dashboard.scenarios"),         value: data.total_scenarios,              icon: Layers,    color: "yellow" },
  ];
  const colorBg   = { blue: "bg-blue-500/10", purple: "bg-purple-500/10", green: "bg-green-500/10", yellow: "bg-yellow-500/10" };
  const colorText = { blue: "text-blue-400",  purple: "text-purple-400",  green: "text-green-400",  yellow: "text-yellow-400" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`p-4 rounded-xl border border-[var(--lm-border)] ${colorBg[k.color]}`}>
            <k.icon className={`w-5 h-5 mb-2 ${colorText[k.color]}`} />
            <p className="text-2xl font-bold text-[var(--lm-text)]">{k.value ?? "0"}</p>
            <p className="text-xs text-[var(--lm-muted)] mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {gap != null && (
        <div className={`p-4 rounded-xl border flex items-center gap-4 ${gap < 0 ? "border-red-500/30 bg-red-500/10" : gap > 0 ? "border-yellow-500/30 bg-yellow-500/10" : "border-green-500/30 bg-green-500/10"}`}>
          {gap < 0 ? <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" /> : gap > 0 ? <TrendingUp className="w-5 h-5 text-yellow-400 flex-shrink-0" /> : <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />}
          <div>
            <p className="font-medium text-[var(--lm-text)]">
              {gap < 0
                ? t("workforcePlanningHub.dashboard.gapBelow", { count: Math.abs(gap) })
                : gap > 0
                  ? t("workforcePlanningHub.dashboard.gapAbove", { count: gap })
                  : t("workforcePlanningHub.dashboard.gapAligned")}
            </p>
            <p className="text-xs text-[var(--lm-muted)]">{t("workforcePlanningHub.dashboard.gapHint")}</p>
          </div>
        </div>
      )}

      {data.budget_used_pct != null && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-[var(--lm-text)]">{t("workforcePlanningHub.dashboard.hrBudgetUsed")}</h3>
            <span className="text-sm font-bold text-[var(--lm-text)]">{data.budget_used_pct}%</span>
          </div>
          <div className="w-full h-3 bg-[var(--lm-bg)] rounded-full overflow-hidden">
            <div className={`h-3 rounded-full ${data.budget_used_pct > 90 ? "bg-red-500" : data.budget_used_pct > 70 ? "bg-yellow-500" : "bg-green-500"}`}
              style={{ width: `${Math.min(data.budget_used_pct, 100)}%` }} />
          </div>
        </div>
      )}

      {data.dept_breakdown?.length > 0 && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <h3 className="font-semibold text-[var(--lm-text)] mb-4">{t("workforcePlanningHub.dashboard.departmentBreakdown")}</h3>
          <div className="space-y-3">
            {data.dept_breakdown.map(d => {
              const maxVal = Math.max(...data.dept_breakdown.map(x => Math.max(x.planned, x.actual || 0)));
              return (
                <div key={d.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-[var(--lm-text)]">{d.name}</span>
                    <div className="flex items-center gap-3 text-xs text-[var(--lm-muted)]">
                      <span className="text-blue-400">{t("workforcePlanningHub.dashboard.plannedLabel", { value: d.planned })}</span>
                      {d.actual != null && <span className="text-green-400">{t("workforcePlanningHub.dashboard.actualLabel", { value: d.actual })}</span>}
                    </div>
                  </div>
                  <div className="relative h-2 bg-[var(--lm-bg)] rounded-full">
                    <div className="absolute h-2 bg-blue-500/40 rounded-full" style={{ width: `${maxVal ? d.planned / maxVal * 100 : 0}%` }} />
                    {d.actual != null && (
                      <div className="absolute h-2 bg-green-500 rounded-full opacity-70" style={{ width: `${maxVal ? d.actual / maxVal * 100 : 0}%` }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-[var(--lm-muted)]">
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-500/40 inline-block" /> {t("workforcePlanningHub.dashboard.planned")}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-green-500 opacity-70 inline-block" /> {t("workforcePlanningHub.dashboard.actual")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Plans Tab ─────────────────────────────────────────────────────────────────

function PlanModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const { config } = useCountryConfig();
  const [form, setForm] = useState({
    name: "", fiscal_year: new Date().getFullYear(),
    start_date: `${new Date().getFullYear()}-01-01`,
    end_date: `${new Date().getFullYear()}-12-31`,
    total_budget: "", currency: "TND", notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const c = config?.currency?.code;
    if (c) setForm(f => (f.currency === "TND" && c !== "TND" ? { ...f, currency: c } : f));
  }, [config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.fiscal_year) { toast.error(t("workforcePlanningHub.plans.requiredMissing")); return; }
    setLoading(true);
    try {
      const payload = { ...form, fiscal_year: parseInt(form.fiscal_year) };
      payload.total_budget = form.total_budget ? parseFloat(form.total_budget) : null;
      await apiClient.post("v1/workforce-planning/plans", payload);
      toast.success(t("workforcePlanningHub.plans.created")); onSaved();
    } catch { toast.error(t("workforcePlanningHub.common.error")); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
          <h2 className="font-semibold text-[var(--lm-text)]">{t("workforcePlanningHub.plans.newHeadcountPlan")}</h2>
          <button onClick={onClose} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.common.name")}</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Plan FY2026" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.plans.fiscalYear")}</label>
              <input type="number" value={form.fiscal_year} onChange={e => setForm(f => ({ ...f, fiscal_year: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.plans.start")}</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.plans.end")}</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.plans.totalBudgetOptional")}</label>
              <input type="number" value={form.total_budget} onChange={e => setForm(f => ({ ...f, total_budget: e.target.value }))}
                placeholder="500000" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.plans.currency")}</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                {["TND","EUR","USD","MAD","QAR","SAR"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("workforcePlanningHub.common.cancel")}</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {loading ? t("workforcePlanningHub.common.creating") : t("workforcePlanningHub.common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlansTab({ onSelectPlan }) {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => apiClient.get("v1/workforce-planning/plans").then(r => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    await apiClient.put(`v1/workforce-planning/plans/${id}`, { status });
    toast.success(t("workforcePlanningHub.plans.statusChanged", { status: statusLabel(status, t).toLowerCase() })); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--lm-muted)]">{t("workforcePlanningHub.plans.count", { count: plans.length })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("workforcePlanningHub.plans.newPlan")}
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("workforcePlanningHub.plans.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map(p => (
            <div key={p.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[var(--lm-text)]">{p.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.status]}`}>{statusLabel(p.status, t)}</span>
                    <span className="text-xs text-[var(--lm-muted)]">FY{p.fiscal_year}</span>
                  </div>
                  <p className="text-xs text-[var(--lm-muted)] mt-1">{p.start_date} → {p.end_date}</p>
                  {p.total_budget && <p className="text-xs text-blue-400 mt-0.5">{t("workforcePlanningHub.plans.budget", { value: fmt(p.total_budget, p.currency, i18n.language) })}</p>}
                  <p className="text-xs text-[var(--lm-muted)] mt-0.5">{t("workforcePlanningHub.plans.entryCount", { count: p.entry_count })}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => onSelectPlan(p.id)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-600/10 text-blue-400 hover:bg-blue-600/20 border border-blue-500/20">
                    {t("workforcePlanningHub.plans.manage")}
                  </button>
                  {p.status === "draft" && (
                    <button onClick={() => changeStatus(p.id, "active")}
                      className="text-xs px-3 py-1.5 rounded-lg bg-green-600/10 text-green-400 hover:bg-green-600/20 border border-green-500/20">
                      {t("workforcePlanningHub.plans.activate")}
                    </button>
                  )}
                  {p.status === "active" && (
                    <button onClick={() => changeStatus(p.id, "locked")}
                      className="text-xs px-3 py-1.5 rounded-lg bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 border border-gray-500/20">
                      {t("workforcePlanningHub.plans.lock")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <PlanModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

// ── Headcount Tab (entries per plan) ──────────────────────────────────────────

function HeadcountTab() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [entries, setEntries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addForm, setAddForm] = useState({
    job_title: "", department_id: "", employment_type: "full_time",
    q1_planned: 0, q2_planned: 0, q3_planned: 0, q4_planned: 0,
    avg_salary: "", priority: "medium", is_backfill: false,
  });

  useEffect(() => {
    apiClient.get("v1/workforce-planning/plans").then(r => { setPlans(r.data); if (r.data.length) setSelectedPlan(r.data[0].id); });
    apiClient.get("v1/organization-chart/departments").then(r => setDepartments(r.data?.departments || r.data || [])).catch(() => {});
  }, []);

  const loadEntries = useCallback(() => {
    if (!selectedPlan) return;
    apiClient.get(`v1/workforce-planning/plans/${selectedPlan}/entries`).then(r => setEntries(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [selectedPlan]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const syncActuals = async () => {
    await apiClient.post(`v1/workforce-planning/plans/${selectedPlan}/sync-actuals`);
    toast.success(t("workforcePlanningHub.headcount.actualsSynced")); loadEntries();
  };

  const addEntry = async (e) => {
    e.preventDefault();
    if (!addForm.job_title) { toast.error(t("workforcePlanningHub.headcount.jobTitleRequired")); return; }
    const payload = {
      ...addForm,
      department_id: addForm.department_id ? parseInt(addForm.department_id) : null,
      q1_planned: parseInt(addForm.q1_planned) || 0,
      q2_planned: parseInt(addForm.q2_planned) || 0,
      q3_planned: parseInt(addForm.q3_planned) || 0,
      q4_planned: parseInt(addForm.q4_planned) || 0,
      avg_salary: addForm.avg_salary ? parseFloat(addForm.avg_salary) : null,
    };
    try {
      await apiClient.post(`v1/workforce-planning/plans/${selectedPlan}/entries`, payload);
      toast.success(t("workforcePlanningHub.headcount.entryAdded")); setShowAdd(false); loadEntries();
    } catch { toast.error(t("workforcePlanningHub.common.error")); }
  };

  const saveEdit = async (id) => {
    await apiClient.put(`v1/workforce-planning/entries/${id}`, editForm);
    toast.success(t("workforcePlanningHub.headcount.entryUpdated")); setEditingId(null); loadEntries();
  };

  const del = async (id) => {
    if (!window.confirm(t("workforcePlanningHub.headcount.deleteConfirm"))) return;
    await apiClient.delete(`v1/workforce-planning/entries/${id}`);
    toast.success(t("workforcePlanningHub.headcount.entryDeleted")); loadEntries();
  };

  const grouped = entries.reduce((acc, e) => {
    const key = e.department_name || t("workforcePlanningHub.headcount.noDepartment");
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <select value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
          <option value="">{t("workforcePlanningHub.headcount.selectPlan")}</option>
          {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {selectedPlan && (
          <>
            <button onClick={syncActuals}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-muted)] hover:text-[var(--lm-text)] text-sm">
              <RefreshCw className="w-4 h-4" /> {t("workforcePlanningHub.headcount.syncActuals")}
            </button>
            <button onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium ml-auto">
              <Plus className="w-4 h-4" /> {t("workforcePlanningHub.headcount.addPosition")}
            </button>
          </>
        )}
      </div>

      {entries.length === 0 && selectedPlan ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("workforcePlanningHub.headcount.empty")}</p>
        </div>
      ) : (
        Object.entries(grouped).map(([dept, deptEntries]) => (
          <div key={dept}>
            <p className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-2">{dept}</p>
            <div className="rounded-xl border border-[var(--lm-border)] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--lm-s1)]">
                    {[
                      t("workforcePlanningHub.headcount.columns.position"),
                      t("workforcePlanningHub.headcount.columns.type"),
                      "Q1",
                      "Q2",
                      "Q3",
                      "Q4",
                      t("workforcePlanningHub.headcount.columns.actual"),
                      t("workforcePlanningHub.headcount.columns.estimatedBudget"),
                      t("workforcePlanningHub.headcount.columns.priority"),
                      "",
                    ].map(h => (
                      <th key={h} className="px-3 py-2 text-xs font-semibold text-[var(--lm-muted)] text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--lm-border)]">
                  {deptEntries.map(e => (
                    <tr key={e.id} className="bg-[var(--lm-bg)] hover:bg-[var(--lm-s1)]">
                      {editingId === e.id ? (
                        <>
                          <td className="px-3 py-2"><input value={editForm.job_title || ""} onChange={ev => setEditForm(f => ({ ...f, job_title: ev.target.value }))}
                            className="w-full px-2 py-1 rounded bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-xs" /></td>
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">{e.employment_type}</td>
                          {["q1_planned","q2_planned","q3_planned","q4_planned"].map(q => (
                            <td key={q} className="px-3 py-2">
                              <input type="number" min={0} value={editForm[q] ?? e[q]} onChange={ev => setEditForm(f => ({ ...f, [q]: parseInt(ev.target.value) }))}
                                className="w-14 px-2 py-1 rounded bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-xs" />
                            </td>
                          ))}
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">{e.q1_actual ?? "—"}</td>
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">—</td>
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">{e.priority}</td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              <button onClick={() => saveEdit(e.id)} className="p-1 text-green-400 hover:bg-green-500/10 rounded"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditingId(null)} className="p-1 text-[var(--lm-muted)] hover:bg-[var(--lm-bg)] rounded"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2">
                            <p className="font-medium text-[var(--lm-text)]">{e.job_title}</p>
                            {e.is_backfill && <span className="text-xs text-orange-400">{t("workforcePlanningHub.headcount.backfill")}</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">{e.employment_type}</td>
                          {[e.q1_planned, e.q2_planned, e.q3_planned, e.q4_planned].map((v, i) => (
                            <td key={i} className="px-3 py-2 text-center font-mono text-sm text-[var(--lm-text)]">{v}</td>
                          ))}
                          <td className="px-3 py-2 text-center">
                            {e.q1_actual != null ? (
                              <span className={`font-mono text-sm ${e.q1_actual >= e.q1_planned ? "text-green-400" : "text-red-400"}`}>{e.q1_actual}</span>
                            ) : <span className="text-[var(--lm-muted)]">—</span>}
                          </td>
                          <td className="px-3 py-2 text-xs text-[var(--lm-muted)]">
                            {e.budget_estimate ? fmt(e.budget_estimate, plans.find(p => String(p.id) === String(selectedPlan))?.currency || "TND", i18n.language) : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-2 py-0.5 rounded border capitalize ${PRIORITY_STYLE[e.priority] || ""}`}>{priorityLabel(e.priority, t)}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              <button onClick={() => { setEditingId(e.id); setEditForm({ job_title: e.job_title, q1_planned: e.q1_planned, q2_planned: e.q2_planned, q3_planned: e.q3_planned, q4_planned: e.q4_planned }); }}
                                className="p-1 text-[var(--lm-muted)] hover:text-[var(--lm-text)] rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                              <button onClick={() => del(e.id)} className="p-1 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-lg shadow-2xl">
            <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("workforcePlanningHub.headcount.addPositionTitle")}</h2>
              <button onClick={() => setShowAdd(false)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <form onSubmit={addEntry} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.headcount.jobTitle")}</label>
                  <input value={addForm.job_title} onChange={e => setAddForm(f => ({ ...f, job_title: e.target.value }))}
                    placeholder={t("workforcePlanningHub.headcount.jobTitlePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.headcount.department")}</label>
                  <select value={addForm.department_id} onChange={e => setAddForm(f => ({ ...f, department_id: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                    <option value="">{t("workforcePlanningHub.common.select")}</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {["q1_planned","q2_planned","q3_planned","q4_planned"].map((q, i) => (
                  <div key={q}>
                    <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">T{i+1}</label>
                    <input type="number" min={0} value={addForm[q]} onChange={e => setAddForm(f => ({ ...f, [q]: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.headcount.averageSalary")}</label>
                  <input type="number" value={addForm.avg_salary} onChange={e => setAddForm(f => ({ ...f, avg_salary: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.headcount.priority")}</label>
                  <select value={addForm.priority} onChange={e => setAddForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                    <option value="high">{t("workforcePlanningHub.priorities.high")}</option>
                    <option value="medium">{t("workforcePlanningHub.priorities.medium")}</option>
                    <option value="low">{t("workforcePlanningHub.priorities.low")}</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={addForm.is_backfill} onChange={e => setAddForm(f => ({ ...f, is_backfill: e.target.checked }))} />
                    <span className="text-sm text-[var(--lm-text)]">{t("workforcePlanningHub.headcount.backfill")}</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("workforcePlanningHub.common.cancel")}</button>
                <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">{t("workforcePlanningHub.common.add")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Scenarios Tab ─────────────────────────────────────────────────────────────

function ScenariosTab() {
  const { t } = useTranslation();
  const [scenarios, setScenarios] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [computed, setComputed] = useState(null);
  const [form, setForm] = useState({ name: "", base_plan_id: "", description: "", growth_rate_pct: "", budget_delta_pct: "", assumptions: [] });
  const [newAssumption, setNewAssumption] = useState("");

  const load = useCallback(() => {
    apiClient.get("v1/workforce-planning/scenarios").then(r => setScenarios(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    apiClient.get("v1/workforce-planning/plans").then(r => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(t("workforcePlanningHub.scenarios.nameRequired")); return; }
    const payload = {
      ...form,
      base_plan_id: form.base_plan_id ? parseInt(form.base_plan_id) : null,
      growth_rate_pct: form.growth_rate_pct ? parseFloat(form.growth_rate_pct) : null,
      budget_delta_pct: form.budget_delta_pct ? parseFloat(form.budget_delta_pct) : null,
    };
    try {
      await apiClient.post("v1/workforce-planning/scenarios", payload);
      toast.success(t("workforcePlanningHub.scenarios.created")); setShowModal(false); load();
    } catch { toast.error(t("workforcePlanningHub.common.error")); }
  };

  const compute = async (id) => {
    const { data } = await apiClient.get(`v1/workforce-planning/scenarios/${id}/compute`);
    setComputed(data);
  };

  const del = async (id) => {
    if (!window.confirm(t("workforcePlanningHub.scenarios.deleteConfirm"))) return;
    await apiClient.delete(`v1/workforce-planning/scenarios/${id}`);
    toast.success(t("workforcePlanningHub.common.deleted")); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--lm-muted)]">{t("workforcePlanningHub.scenarios.count", { count: scenarios.length })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("workforcePlanningHub.scenarios.newScenario")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          {scenarios.length === 0 ? (
            <div className="text-center py-16 text-[var(--lm-muted)]">
              <Layers className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>{t("workforcePlanningHub.scenarios.empty")}</p>
            </div>
          ) : scenarios.map(s => (
            <div key={s.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-[var(--lm-text)]">{s.name}</p>
                  {s.description && <p className="text-xs text-[var(--lm-muted)] mt-0.5">{s.description}</p>}
                  <div className="flex gap-3 mt-2 text-xs">
                    {s.growth_rate_pct != null && (
                      <span className={`flex items-center gap-1 ${s.growth_rate_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {s.growth_rate_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {t("workforcePlanningHub.scenarios.headcountDelta", { value: `${s.growth_rate_pct >= 0 ? "+" : ""}${s.growth_rate_pct}` })}
                      </span>
                    )}
                    {s.budget_delta_pct != null && (
                      <span className={`flex items-center gap-1 ${s.budget_delta_pct >= 0 ? "text-yellow-400" : "text-blue-400"}`}>
                        {t("workforcePlanningHub.scenarios.budgetDelta", { value: `${s.budget_delta_pct >= 0 ? "+" : ""}${s.budget_delta_pct}` })}
                      </span>
                    )}
                  </div>
                  {s.assumptions?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {s.assumptions.slice(0,3).map((a, i) => (
                        <p key={i} className="text-xs text-[var(--lm-muted)]">• {a}</p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {s.base_plan_id && (
                    <button onClick={() => compute(s.id)}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-purple-600/10 text-purple-400 hover:bg-purple-600/20 border border-purple-500/20">
                      <Zap className="w-3 h-3" /> {t("workforcePlanningHub.scenarios.compute")}
                    </button>
                  )}
                  <button onClick={() => del(s.id)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {computed && (
          <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[var(--lm-text)]">{computed.scenario?.name}</h3>
              <button onClick={() => setComputed(null)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                [t("workforcePlanningHub.scenarios.base"), computed.total_base_headcount, "text-[var(--lm-muted)]"],
                [t("workforcePlanningHub.scenarios.projected"), computed.total_projected_headcount, "text-blue-400"],
                [t("workforcePlanningHub.scenarios.delta"), `${computed.headcount_delta >= 0 ? "+" : ""}${computed.headcount_delta}`, computed.headcount_delta >= 0 ? "text-green-400" : "text-red-400"],
              ].map(([label, val, cls]) => (
                <div key={label} className="p-3 rounded-lg bg-[var(--lm-bg)]">
                  <p className={`text-2xl font-bold ${cls}`}>{val}</p>
                  <p className="text-xs text-[var(--lm-muted)]">{label}</p>
                </div>
              ))}
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {computed.entries?.slice(0,15).map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-[var(--lm-bg)]">
                  <span className="text-[var(--lm-text)]">{e.job_title}</span>
                  <div className="flex gap-3 text-[var(--lm-muted)]">
                    <span>{t("workforcePlanningHub.scenarios.baseValue", { value: e.q1_base })}</span>
                    <span className="text-blue-400">→ {e.q1_projected}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("workforcePlanningHub.scenarios.newScenario")}</h2>
              <button onClick={() => setShowModal(false)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <form onSubmit={create} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.common.name")}</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={t("workforcePlanningHub.scenarios.namePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.scenarios.basePlan")}</label>
                <select value={form.base_plan_id} onChange={e => setForm(f => ({ ...f, base_plan_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                  <option value="">{t("workforcePlanningHub.common.optional")}</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.scenarios.headcountVariation")}</label>
                  <input type="number" step="0.1" value={form.growth_rate_pct} onChange={e => setForm(f => ({ ...f, growth_rate_pct: e.target.value }))}
                    placeholder="+10" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.scenarios.budgetVariation")}</label>
                  <input type="number" step="0.1" value={form.budget_delta_pct} onChange={e => setForm(f => ({ ...f, budget_delta_pct: e.target.value }))}
                    placeholder="-5" className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("workforcePlanningHub.scenarios.assumptions")}</label>
                <div className="flex gap-2">
                  <input value={newAssumption} onChange={e => setNewAssumption(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); if (newAssumption.trim()) { setForm(f => ({ ...f, assumptions: [...f.assumptions, newAssumption.trim()] })); setNewAssumption(""); } } }}
                    placeholder={t("workforcePlanningHub.scenarios.assumptionPlaceholder")} className="flex-1 px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                {form.assumptions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {form.assumptions.map((a, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-[var(--lm-muted)]">• {a}</span>
                        <button type="button" onClick={() => setForm(f => ({ ...f, assumptions: f.assumptions.filter((_, j) => j !== i) }))}
                          className="text-red-400 hover:text-red-300 ml-auto"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("workforcePlanningHub.common.cancel")}</button>
                <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">{t("workforcePlanningHub.common.create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hub ───────────────────────────────────────────────────────────────────────

export default function WorkforcePlanningHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard":  return <DashboardTab />;
      case "plans":      return <PlansTab onSelectPlan={id => setSearchParams({ tab: "headcount", plan: id })} />;
      case "headcount":  return <HeadcountTab />;
      case "scenarios":  return <ScenariosTab />;
      default:           return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
          <Users className="w-6 h-6 text-indigo-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--lm-text)]">{t("workforcePlanningHub.title")}</h1>
          <p className="text-sm text-[var(--lm-muted)]">{t("workforcePlanningHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--lm-border)] overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? "border-indigo-500 text-indigo-400" : "border-transparent text-[var(--lm-muted)] hover:text-[var(--lm-text)]"
              }`}>
              <Icon className="w-4 h-4" />
              {t(`workforcePlanningHub.tabs.${tab.id}`)}
            </button>
          );
        })}
      </div>

      {renderTab()}
    </div>
  );
}
